import React from "react";

export const ANALYTICS_SERIES_COLORS = ["#2563EB", "#0EA5E9", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#14B8A6", "#F97316"];

export function safeNumber(value) {
  return Number(value) || 0;
}

export function truncateText(value, maxLength) {
  if (!value || value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

export function formatNumber(value, locale = "en") {
  return new Intl.NumberFormat(locale).format(safeNumber(value));
}

export function formatPercentage(value, locale = "en") {
  return `${new Intl.NumberFormat(locale).format(safeNumber(value))}%`;
}

export function parseDateValue(dateValue) {
  if (!dateValue || typeof dateValue !== "string") return null;

  const parts = dateValue.split("-");
  if (parts.length !== 3) return null;

  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

export function formatDateValue(dateValue, locale, options) {
  const parsed = parseDateValue(dateValue);
  if (!parsed) return dateValue;

  return new Intl.DateTimeFormat(locale, options).format(parsed);
}

export function formatRangeLabel(startDate, endDate, locale) {
  if (!startDate || !endDate) return "";
  if (startDate === endDate) {
    return formatDateValue(startDate, locale, { day: "2-digit", month: "short", year: "numeric" });
  }

  return `${formatDateValue(startDate, locale, { day: "2-digit", month: "short" })} - ${formatDateValue(endDate, locale, { day: "2-digit", month: "short", year: "numeric" })}`;
}

export function getRangeDayCount(startDate, endDate) {
  const start = parseDateValue(startDate);
  const end = parseDateValue(endDate);

  if (!start || !end) return 0;

  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  return Math.max(1, Math.round((end - start) / millisecondsPerDay) + 1);
}

export function getTopItem(items, valueKey = "value") {
  if (!items?.length) return null;

  return items.reduce((top, item) => (safeNumber(item[valueKey]) > safeNumber(top[valueKey]) ? item : top), items[0]);
}

export function getPeakEntry(items, valueKey = "count") {
  if (!items?.length) return null;

  return items.reduce((peak, item) => (safeNumber(item[valueKey]) > safeNumber(peak[valueKey]) ? item : peak), items[0]);
}

export function getKpiValue(kpis, id) {
  const matchedKpi = (kpis || []).find((kpi) => kpi.id === id);
  return safeNumber(matchedKpi?.value);
}

export function buildDistributionSummary(items, locale, unitLabel, options = {}) {
  if (!items?.length) return [];

  const {
    labelKey = "label",
    valueKey = "value",
    limit = 3,
    hintFormatter,
  } = options;
  const numberFormatter = new Intl.NumberFormat(locale);
  const total = items.reduce((sum, item) => sum + safeNumber(item[valueKey]), 0);

  return [...items]
    .sort((left, right) => safeNumber(right[valueKey]) - safeNumber(left[valueKey]))
    .slice(0, limit)
    .map((item) => {
      const value = safeNumber(item[valueKey]);
      const percentage = total > 0 ? `${Math.round((value / total) * 100)}%` : "0%";

      return {
        label: item[labelKey],
        value: percentage,
        hint: hintFormatter ? hintFormatter(item, numberFormatter) : unitLabel ? `${numberFormatter.format(value)} ${unitLabel}` : numberFormatter.format(value),
      };
    });
}

function TooltipCard({ title, subtitle, value }) {
  return (
    <div className="analytics-tooltip">
      {title && <strong className="analytics-tooltip__title">{title}</strong>}
      {subtitle && <span className="analytics-tooltip__subtitle">{subtitle}</span>}
      {value && <span className="analytics-tooltip__value">{value}</span>}
    </div>
  );
}

export function TimeSeriesTooltip({ active, payload, label, locale, unitLabel, dateOptions = { day: "2-digit", month: "short", year: "numeric" } }) {
  if (!active || !payload?.length) return null;

  return (
    <TooltipCard
      title={label ? formatDateValue(label, locale, dateOptions) : null}
      value={`${formatNumber(payload[0].value, locale)} ${unitLabel}`.trim()}
    />
  );
}

export function DistributionTooltip({ active, payload, locale, unitLabel, labelKey = "label", subtitleKey = null, valueKey = "value" }) {
  if (!active || !payload?.length) return null;

  const entry = payload[0].payload;

  return (
    <TooltipCard
      title={entry[labelKey]}
      subtitle={subtitleKey ? entry[subtitleKey] : null}
      value={`${formatNumber(entry[valueKey], locale)} ${unitLabel}`.trim()}
    />
  );
}

export function buildPieLabelRenderer({ maxLength = 16, minPercent = 0.07, showLabel = true, showPercent = true, fontSize = 11 } = {}) {
  const RADIAN = Math.PI / 180;

  return ({ cx, cy, midAngle, innerRadius, outerRadius, percent, payload }) => {
    if (!payload || percent < minPercent) return null;

    const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const label = truncateText(payload.label, maxLength);
    const hasLabel = showLabel && label;
    const hasPercent = showPercent;

    if (!hasLabel && !hasPercent) return null;

    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize, fontWeight: 700 }}
      >
        {hasLabel && <tspan x={x} dy={hasPercent ? "-0.1em" : "0"}>{label}</tspan>}
        {hasPercent && <tspan x={x} dy={hasLabel ? "1.2em" : "0"}>{`${Math.round(percent * 100)}%`}</tspan>}
      </text>
    );
  };
}
